import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { UsersThree, Envelope, Trash, ShieldCheck, PaperPlaneTilt, CopySimple } from "@phosphor-icons/react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";

function formatApiError(detail) {
  if (detail == null) return "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  if (detail?.msg) return detail.msg;
  return String(detail);
}

export default function Team() {
  const { user, refresh } = useAuth();
  const [data, setData] = useState(null);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState("member");
  const [busy, setBusy] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [newName, setNewName] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/company/me");
      setData(data);
      setNewName(data?.company?.name || "");
    } catch (e) {
      toast.error("Failed to load team.");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const isOwner = data?.you?.company_role === "owner";

  const submitInvite = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      await api.post("/company/invite", { email: inviteEmail, role: inviteRole, origin: window.location.origin });
      toast.success(`Invite sent to ${inviteEmail}`);
      setInviteEmail("");
      load();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const revoke = async (invite_id) => {
    if (!window.confirm("Revoke this invite?")) return;
    try {
      await api.delete(`/company/invites/${invite_id}`);
      toast.success("Invite revoked");
      load();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    }
  };

  const remove = async (uid, name) => {
    if (!window.confirm(`Remove ${name} from the workspace?`)) return;
    try {
      await api.delete(`/company/members/${uid}`);
      toast.success("Member removed");
      load();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    }
  };

  const changeRole = async (uid, role) => {
    try {
      await api.patch(`/company/members/${uid}/role`, { role });
      toast.success("Role updated");
      load();
      refresh();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    }
  };

  const rename = async () => {
    if (!newName.trim()) return;
    try {
      await api.patch("/company/rename", { name: newName.trim() });
      toast.success("Workspace renamed");
      setRenaming(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    }
  };

  const copyInviteLink = (invite_id) => {
    // Just a nudge — the token isn't returned; owner should resend via email
    toast.info("Invitation is sent by email. Ask the teammate to check spam if it hasn't arrived.");
  };

  if (!data) {
    return (
      <div className="App min-h-screen flex flex-col">
        <Navbar />
        <section className="flex-1 grid place-items-center text-sm text-muted-foreground">Loading team…</section>
        <Footer />
      </div>
    );
  }

  return (
    <div className="App min-h-screen flex flex-col">
      <Navbar />
      <section className="flex-1 max-w-5xl w-full mx-auto px-6 lg:px-8 py-10">
        <div className="flex items-start justify-between gap-4 mb-8">
          <div>
            <div className="text-xs tracking-widest-2 uppercase text-muted-foreground font-semibold mb-2 flex items-center gap-2">
              <UsersThree size={14} weight="duotone" /> Team
            </div>
            {renaming ? (
              <div className="flex items-center gap-2">
                <Input value={newName} onChange={(e) => setNewName(e.target.value)} className="rounded-sm h-11 w-72" data-testid="team-rename-input" />
                <Button size="sm" className="rounded-sm" onClick={rename} data-testid="team-rename-save">Save</Button>
                <Button size="sm" variant="ghost" onClick={() => { setRenaming(false); setNewName(data.company.name); }}>Cancel</Button>
              </div>
            ) : (
              <h1 className="font-heading text-3xl md:text-4xl font-bold tracking-tighter" data-testid="team-company-name">
                {data.company?.name || "Your workspace"}
              </h1>
            )}
            <p className="text-sm text-muted-foreground mt-2 max-w-xl">
              Everyone in this workspace can see the team's audience requests and orders and place new ones. Only owners can invite or remove teammates.
            </p>
          </div>
          {isOwner && !renaming && (
            <Button variant="outline" className="rounded-sm" onClick={() => setRenaming(true)} data-testid="team-rename-btn">Rename workspace</Button>
          )}
        </div>

        {/* Invite */}
        {isOwner && (
          <div className="border border-border bg-white p-6 mb-8">
            <h2 className="font-heading text-lg font-semibold mb-1">Invite a teammate</h2>
            <p className="text-sm text-muted-foreground mb-4">They'll get an email with an invitation link that expires in 7 days.</p>
            <form onSubmit={submitInvite} className="flex flex-wrap gap-3 items-end" data-testid="team-invite-form">
              <div className="flex-1 min-w-[240px]">
                <Label htmlFor="invite-email" className="text-xs">Work email</Label>
                <div className="relative mt-1.5">
                  <Envelope size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    id="invite-email"
                    type="email"
                    required
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    placeholder="teammate@company.com"
                    className="pl-9 rounded-sm h-11"
                    data-testid="team-invite-email"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="invite-role" className="text-xs">Role</Label>
                <select
                  id="invite-role"
                  value={inviteRole}
                  onChange={(e) => setInviteRole(e.target.value)}
                  className="mt-1.5 h-11 rounded-sm border border-input bg-white px-3 text-sm"
                  data-testid="team-invite-role"
                >
                  <option value="member">Member</option>
                  <option value="owner">Owner</option>
                </select>
              </div>
              <Button type="submit" disabled={busy} className="rounded-sm h-11" data-testid="team-invite-submit">
                <PaperPlaneTilt size={16} className="mr-2" />
                {busy ? "Sending…" : "Send invite"}
              </Button>
            </form>
          </div>
        )}

        {/* Members */}
        <div className="border border-border bg-white mb-8">
          <div className="border-b border-border p-4 flex items-center justify-between">
            <h2 className="font-heading text-lg font-semibold">Members ({data.members?.length || 0})</h2>
          </div>
          <table className="w-full text-sm" data-testid="team-members-table">
            <thead className="bg-muted/40 text-[10px] font-mono uppercase tracking-widest-2 text-muted-foreground">
              <tr>
                <th className="p-3 text-left">Name</th>
                <th className="p-3 text-left">Email</th>
                <th className="p-3 text-left">Role</th>
                <th className="p-3 text-left">Joined</th>
                <th className="p-3"></th>
              </tr>
            </thead>
            <tbody>
              {(data.members || []).map((m) => (
                <tr key={m.user_id} className="border-t border-border" data-testid={`team-member-${m.user_id}`}>
                  <td className="p-3 font-medium">
                    {m.name}
                    {m.user_id === data.you?.user_id && <span className="ml-2 text-[10px] font-mono uppercase text-muted-foreground">you</span>}
                  </td>
                  <td className="p-3 text-muted-foreground">
                    {m.email}
                    {m.email_verified === false && <span className="ml-2 text-[10px] font-mono uppercase text-amber-700">unverified</span>}
                  </td>
                  <td className="p-3">
                    {isOwner && m.user_id !== data.you?.user_id ? (
                      <select
                        value={m.company_role || "member"}
                        onChange={(e) => changeRole(m.user_id, e.target.value)}
                        className="h-8 rounded-sm border border-input bg-white px-2 text-xs"
                        data-testid={`team-role-select-${m.user_id}`}
                      >
                        <option value="member">Member</option>
                        <option value="owner">Owner</option>
                      </select>
                    ) : (
                      <span className="text-xs font-mono uppercase tracking-widest-2 text-muted-foreground">{m.company_role || "member"}</span>
                    )}
                  </td>
                  <td className="p-3 text-muted-foreground text-xs">{(m.created_at || "").slice(0, 10)}</td>
                  <td className="p-3 text-right">
                    {isOwner && m.user_id !== data.you?.user_id && (
                      <button
                        onClick={() => remove(m.user_id, m.name)}
                        className="text-muted-foreground hover:text-red-600"
                        data-testid={`team-remove-${m.user_id}`}
                        title="Remove from workspace"
                      >
                        <Trash size={16} />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pending invites */}
        <div className="border border-border bg-white">
          <div className="border-b border-border p-4 flex items-center justify-between">
            <h2 className="font-heading text-lg font-semibold">Pending invites ({data.pending_invites?.length || 0})</h2>
          </div>
          {(data.pending_invites || []).length === 0 ? (
            <div className="p-6 text-sm text-muted-foreground" data-testid="team-invites-empty">No pending invites.</div>
          ) : (
            <table className="w-full text-sm" data-testid="team-invites-table">
              <thead className="bg-muted/40 text-[10px] font-mono uppercase tracking-widest-2 text-muted-foreground">
                <tr>
                  <th className="p-3 text-left">Email</th>
                  <th className="p-3 text-left">Role</th>
                  <th className="p-3 text-left">Invited</th>
                  <th className="p-3 text-left">Expires</th>
                  <th className="p-3"></th>
                </tr>
              </thead>
              <tbody>
                {data.pending_invites.map((inv) => (
                  <tr key={inv.invite_id} className="border-t border-border" data-testid={`team-invite-${inv.invite_id}`}>
                    <td className="p-3">{inv.email}</td>
                    <td className="p-3 text-xs font-mono uppercase tracking-widest-2 text-muted-foreground">{inv.role}</td>
                    <td className="p-3 text-xs text-muted-foreground">{(inv.created_at || "").slice(0, 10)}</td>
                    <td className="p-3 text-xs text-muted-foreground">{(inv.expires_at || "").slice(0, 10)}</td>
                    <td className="p-3 text-right">
                      {isOwner && (
                        <div className="flex gap-2 justify-end">
                          <button
                            onClick={() => copyInviteLink(inv.invite_id)}
                            className="text-muted-foreground hover:text-foreground"
                            title="Invitation was sent via email"
                          >
                            <CopySimple size={16} />
                          </button>
                          <button
                            onClick={() => revoke(inv.invite_id)}
                            className="text-muted-foreground hover:text-red-600"
                            data-testid={`team-invite-revoke-${inv.invite_id}`}
                            title="Revoke invitation"
                          >
                            <Trash size={16} />
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="mt-8 text-xs text-muted-foreground flex items-center gap-2">
          <ShieldCheck size={14} /> Ownership can be transferred at any time from this page.
        </div>
      </section>
      <Footer />
    </div>
  );
}
